/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { AnyAction } from 'redux';

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';
import { DateTime } from 'src/core/utils';

import { DEFAULT_RETRY_OPTIONS, ONE_LINE_GUIDE_DISPLAY_TIME } from '../../../config';
import { OneLineGuideCell } from '../../../core/entities';
import { BrandingUiInfoKeys } from '../../../core/entities/BrandingUiInfoKeys';
import { Channel } from '../../../core/entities/Channel';
import { InteractiveComponents } from '../../../core/enums';
import { GuideError, GuideRowsError, TypedChannelsNotFoundError } from '../../../core/error';
import { ServicesClientImpl } from '../../../core/interactors';
import { Locale, LocaleKeys } from '../../../core/locale';
import { Log, OperationsLogger } from '../../../core/logging';
import { ScreenTaskControllerTypes } from '../../../core/navigation/ScreenTaskControllerTypes';
import { NetworkManager } from '../../../core/network/NetworkManager';
import { RetryMechanism } from '../../../core/retrymechanism/RetryMechanism';
import { RetryOptions } from '../../../core/retrymechanism/RetryOptions';
import { TOfferPreview, TOneLineGuideRow } from '../../../core/types';
import { TChannels } from '../../../core/types/TChannels';
import { UniqueIdentifier } from '../../../core/utils/UniqueIdentifier';
import { VideoPlaybackController } from '../../../core/videoplayer/VideoPlaybackController';
import { setScreenTaskController } from '../../screens/screens.reducers';
import { hideWhisper, showWhisper } from '../../whisper/whisper.reducer';
import { setDefaultVideoPlayerTask } from '../infobanner/infobanner.reducers';
import {
    setOneLineGuideActiveAction,
    setOneLineGuideAreFutureCellsReceived,
    setOneLineGuideArePastCellsReceived,
    setOneLineGuideCellAiringNowAction,
    setOneLineGuideCellsAppendAction,
    setOneLineGuideCellsDeleteAction,
    setOneLineGuideCellSelectedAction,
    setOneLineGuideChannelsAction,
    setOneLineGuideCurrentlyAiringChannelAction,
    setOneLineGuideEmptyAction,
    setOneLineGuideError,
    setOneLineGuideHidden,
    setOneLineGuideInactiveAction,
    setOneLineGuideIsContentFetched,
    setOneLineGuideIsFutureModeAction,
    setOneLineGuideSelectedChannelAction,
    setOneLineGuideTimeStamp,
    updateOneLineGuideAiringNowTimeoutAction,
    updateOneLineGuideInactivityTimeoutAction,
    updateOneLineGuideStickinessTimeoutAction,
} from './onelineguide.actions';
import {
    ONE_LINE_GUIDE_CHANNEL_SLICE_SIZE,
    ONE_LINE_GUIDE_DEFAULT_FUTURE_DAYS,
    ONE_LINE_GUIDE_DEFAULT_PAST_DAYS,
    ONE_LINE_GUIDE_FETCH_ASSETS_NUMBER,
    ONE_LINE_GUIDE_OFFSET_FROM_ORIGIN,
    ONE_LINE_GUIDE_STICKINESS_DURATION,
    TAG,
} from './onelineguide.constants';
import {
    SET_ONE_LINE_GUIDE_ACTIVE,
    SET_ONE_LINE_GUIDE_ARE_CHANNELS_SELECTED,
    SET_ONE_LINE_GUIDE_ARE_FUTURE_CELLS_RECEIVED,
    SET_ONE_LINE_GUIDE_ARE_PAST_CELLS_RECEIVED,
    SET_ONE_LINE_GUIDE_CELL_AIRING_NOW,
    SET_ONE_LINE_GUIDE_CELL_SELECTED,
    SET_ONE_LINE_GUIDE_CELLS_APPEND,
    SET_ONE_LINE_GUIDE_CELLS_DELETE,
    SET_ONE_LINE_GUIDE_CELLS_PREPEND,
    SET_ONE_LINE_GUIDE_CHANNELS,
    SET_ONE_LINE_GUIDE_CURRENT_OFFER_PREVIEW,
    SET_ONE_LINE_GUIDE_CURRENT_SELECTED_CELL_STAR_RATING,
    SET_ONE_LINE_GUIDE_CURRENTLY_AIRING_CHANNEL,
    SET_ONE_LINE_GUIDE_EMPTY,
    SET_ONE_LINE_GUIDE_ERROR,
    SET_ONE_LINE_GUIDE_HIDDEN,
    SET_ONE_LINE_GUIDE_HORIZONTAL_SCROLL,
    SET_ONE_LINE_GUIDE_INACTIVE,
    SET_ONE_LINE_GUIDE_IS_CONTENT_FETCHED,
    SET_ONE_LINE_GUIDE_IS_FUTURE_MODE,
    SET_ONE_LINE_GUIDE_SELECTED_CHANNEL,
    SET_ONE_LINE_GUIDE_TIME_STAMP,
    SET_ONE_LINE_GUIDE_TURBO_SCROLL,
    UPDATE_ONE_LINE_GUIDE_AIRING_NOW_TIMEOUT,
    UPDATE_ONE_LINE_GUIDE_INACTIVITY_TIMEOUT,
    UPDATE_ONE_LINE_GUIDE_STICKINESS_TIMEOUT,
} from './onelineguide.types';

interface OneLineGuideReducerState {
    cellList: OneLineGuideCell[];
    channels: Channel[];
    currentAiringCell: OneLineGuideCell | null;
    currentSelectedCell: OneLineGuideCell | null;
    currentSelectedCellStarRating: number | null;
    areChannelsSelected: boolean;
    isFutureMode: boolean;
    isContentFetched: boolean;
    offerPreview: Promise<TOfferPreview> | null;
    oneLineGuideAiringNowTimeout: NodeJS.Timeout | null;
    oneLineGuideInactivityTimeout: NodeJS.Timeout | null;
    oneLineGuideStickinessTimeout: NodeJS.Timeout | null;
    showOneLineGuide: boolean;
    currentlyAiringChannel: Channel | null;
    selectedChannel: Channel | null;
    timeStamp: DateTime | null;
    totalChannelsNumber: number;
    error: GuideError | null;
    oneLineGuideTurboScroll: boolean;
    oneLineGuideTurboScrollDirection: InteractiveComponents;
    oneLineGuideHorizontalDirection: null | InteractiveComponents;
    arePastCellsReceived: boolean;
    areFutureCellsReceived: boolean;
    isEmpty: boolean;
}

const initialState: OneLineGuideReducerState = {
    cellList: [],
    channels: [],
    currentAiringCell: null,
    currentSelectedCell: null,
    currentSelectedCellStarRating: null,
    offerPreview: null,
    oneLineGuideAiringNowTimeout: null,
    oneLineGuideInactivityTimeout: null,
    oneLineGuideStickinessTimeout: null,
    areChannelsSelected: false,
    isFutureMode: true,
    isContentFetched: false,
    showOneLineGuide: false,
    currentlyAiringChannel: null,
    selectedChannel: null,
    totalChannelsNumber: 0,
    timeStamp: null,
    error: null,
    oneLineGuideTurboScroll: false,
    oneLineGuideTurboScrollDirection: InteractiveComponents.NORTH,
    oneLineGuideHorizontalDirection: null,
    arePastCellsReceived: false,
    areFutureCellsReceived: false,
    isEmpty: false,
};

const oneLineGuideReducers = (state = initialState, { type, payload } = <AnyAction>{}) => {
    switch (type) {
        case SET_ONE_LINE_GUIDE_ACTIVE:
            return { ...state, showOneLineGuide: true };

        case SET_ONE_LINE_GUIDE_CELLS_PREPEND:
            return {
                ...state,
                cellList: [...payload.cells, ...state.cellList],
            };

        case SET_ONE_LINE_GUIDE_CELLS_APPEND:
            return {
                ...state,
                cellList: [...state.cellList, ...payload.cells],
            };

        case SET_ONE_LINE_GUIDE_CELL_AIRING_NOW:
            return {
                ...state,
                currentAiringCell: payload.cell,
            };

        case SET_ONE_LINE_GUIDE_CELL_SELECTED:
            return {
                ...state,
                currentSelectedCell: payload.cell,
            };

        case SET_ONE_LINE_GUIDE_CURRENT_SELECTED_CELL_STAR_RATING:
            return {
                ...state,
                currentSelectedCellStarRating: payload.rating,
            };

        case SET_ONE_LINE_GUIDE_CURRENTLY_AIRING_CHANNEL:
            return {
                ...state,
                currentlyAiringChannel: payload.channel,
            };

        case SET_ONE_LINE_GUIDE_CHANNELS:
            return {
                ...state,
                channels: payload.channels,
            };

        case SET_ONE_LINE_GUIDE_CURRENT_OFFER_PREVIEW:
            return {
                ...state,
                offerPreview: payload.offerPreview,
            };

        case SET_ONE_LINE_GUIDE_EMPTY:
            return {
                ...state,
                isEmpty: payload.isEmpty,
            };

        case SET_ONE_LINE_GUIDE_ERROR:
            return {
                ...state,
                error: payload.error,
            };

        case SET_ONE_LINE_GUIDE_INACTIVE:
            return {
                ...state,
                showOneLineGuide: false,
                areChannelsSelected: false,
                isFutureMode: true,
                isContentFetched: false,
            };

        case SET_ONE_LINE_GUIDE_ARE_CHANNELS_SELECTED:
            return { ...state, areChannelsSelected: payload.areChannelsSelected };

        case SET_ONE_LINE_GUIDE_IS_CONTENT_FETCHED:
            return { ...state, isContentFetched: payload.isContentFetched };

        case SET_ONE_LINE_GUIDE_IS_FUTURE_MODE:
            return { ...state, isFutureMode: payload.isFutureMode };

        case SET_ONE_LINE_GUIDE_HIDDEN:
            return {
                ...state,
                showOneLineGuide: false,
            };

        case SET_ONE_LINE_GUIDE_CELLS_DELETE:
            return {
                ...state,
                cellList: [],
                currentAiringCell: null,
                currentSelectedCell: null,
            };

        case SET_ONE_LINE_GUIDE_SELECTED_CHANNEL:
            return {
                ...state,
                selectedChannel: payload.selectedChannel,
            };

        case SET_ONE_LINE_GUIDE_TIME_STAMP:
            return {
                ...state,
                timeStamp: payload.timeStamp,
            };

        case SET_ONE_LINE_GUIDE_ARE_PAST_CELLS_RECEIVED:
            return {
                ...state,
                arePastCellsReceived: payload.arePastCellsReceived,
            };

        case SET_ONE_LINE_GUIDE_ARE_FUTURE_CELLS_RECEIVED:
            return {
                ...state,
                areFutureCellsReceived: payload.areFutureCellsReceived,
            };

        case UPDATE_ONE_LINE_GUIDE_AIRING_NOW_TIMEOUT:
            return {
                ...state,
                oneLineGuideAiringNowTimeout: payload.oneLineGuideAiringNowTimeout,
            };

        case UPDATE_ONE_LINE_GUIDE_INACTIVITY_TIMEOUT:
            return {
                ...state,
                oneLineGuideInactivityTimeout: payload.oneLineGuideInactivityTimeout,
            };

        case UPDATE_ONE_LINE_GUIDE_STICKINESS_TIMEOUT:
            return {
                ...state,
                oneLineGuideStickinessTimeout: payload.oneLineGuideStickinessTimeout,
            };
        case SET_ONE_LINE_GUIDE_TURBO_SCROLL:
            return {
                ...state,
                oneLineGuideTurboScroll: payload.oneLineGuideTurboScroll,
                oneLineGuideTurboScrollDirection: payload.oneLineGuideTurboScrollDirection || null,
            };
        case SET_ONE_LINE_GUIDE_HORIZONTAL_SCROLL:
            return {
                ...state,
                oneLineGuideHorizontalDirection: payload.oneLineGuideHorizontalDirection || null,
            };
        default:
            return state;
    }
};

export default oneLineGuideReducers;

export const showOneLineGuide = () => async (dispatch: Function, getState: Function) => {
    const { isOverlayDialogActive } = getState().overlayDialog;
    const videoPlayerInstance = VideoPlaybackController.getInstance();

    const currentChannelNumber = videoPlayerInstance.getCurrentChannelNumber();
    const lastPlayedChannel =
        await new ServicesClientImpl().getLastPlayedOrFallbackToFirstByChannelNumber(
            currentChannelNumber,
        );
    const channel = videoPlayerInstance.getPlayable()?.channel || lastPlayedChannel?.channel;
    const { channels } = getState().gridGuide;
    const {
        oneLineGuideStickinessTimeout,
        currentSelectedCell,
        // In this case, the currentlyAiringChannel resembles
        // the last airing channel until setOneLineGuideCurrentlyAiringChannelAction is dispatched
        currentlyAiringChannel: lastAiringChannel,
    } = getState().oneLineGuide;

    dispatch(hideWhisper());
    if (NetworkManager.isNetworkDisconnected()) {
        dispatch(showWhisper());
    }

    const channelObject = channels.find((ch) => ch.callSign === channel?.callSign);

    const channelToSetAsCurrentlyAiring = channelObject || channel;
    const openedTheSameChannel =
        lastAiringChannel && lastAiringChannel.channelId === channelObject?.channelId;

    if (oneLineGuideStickinessTimeout) clearTimeout(oneLineGuideStickinessTimeout);

    if (!isOverlayDialogActive && channel) {
        dispatch(setOneLineGuideActiveAction());
        dispatch(setScreenTaskController(ScreenTaskControllerTypes.ONE_LINE_GUIDE_TASKS));
        dispatch(updateOneLineGuideInactivityTimeout());
        dispatch(setOneLineGuideCurrentlyAiringChannelAction(channelToSetAsCurrentlyAiring));
        if (!currentSelectedCell || !openedTheSameChannel) {
            dispatch(setOneLineGuideSelectedChannelAction(channelToSetAsCurrentlyAiring));
            dispatch(getOneLineGuideChannels(channelToSetAsCurrentlyAiring));
            dispatch(getMetadataForChannel(channelToSetAsCurrentlyAiring, new DateTime()));
            dispatch(setOneLineGuideTimeStamp(new DateTime()));
        }
    }
};

export const hideOneLineGuide = () => (dispatch: Function, getState: Function) => {
    const { oneLineGuide } = getState();
    const { oneLineGuideInactivityTimeout } = oneLineGuide;

    if (oneLineGuideInactivityTimeout) clearTimeout(oneLineGuideInactivityTimeout);

    dispatch(setOneLineGuideHidden());
    dispatch(updateOneLineGuideStickinessTimeout());
    dispatch(setDefaultVideoPlayerTask());
    dispatch(hideWhisper());

    Diagnostics.info(TAG, 'hideOneLineGuide');
    Log.i(TAG, 'hideOneLineGuide');
};

export const updateOneLineGuideInactivityTimeout =
    () => (dispatch: Function, getState: Function) => {
        const { oneLineGuide } = getState();
        const { oneLineGuideInactivityTimeout } = oneLineGuide;

        if (oneLineGuideInactivityTimeout) clearTimeout(oneLineGuideInactivityTimeout);

        const timer = setTimeout(() => {
            dispatch(hideOneLineGuide());
        }, ONE_LINE_GUIDE_DISPLAY_TIME);

        dispatch(updateOneLineGuideInactivityTimeoutAction(timer));

        Diagnostics.info(TAG, 'updateOneLineGuideInactivityTimeout');
        Log.i(TAG, 'updateOneLineGuideInactivityTimeout');
    };

export const updateOneLineGuideStickinessTimeout =
    () => (dispatch: Function, getState: Function) => {
        const { oneLineGuide } = getState();
        const { oneLineGuideStickinessTimeout, oneLineGuideAiringNowTimeout } = oneLineGuide;

        if (oneLineGuideStickinessTimeout) clearTimeout(oneLineGuideStickinessTimeout);

        const timer = setTimeout(() => {
            if (oneLineGuideAiringNowTimeout) clearTimeout(oneLineGuideAiringNowTimeout);

            dispatch(setOneLineGuideInactiveAction());
            dispatch(setOneLineGuideCellsDeleteAction());
        }, ONE_LINE_GUIDE_STICKINESS_DURATION);

        dispatch(updateOneLineGuideStickinessTimeoutAction(timer));

        Diagnostics.info(TAG, 'updateOneLineGuideStickinessTimeout');
        Log.i(TAG, 'updateOneLineGuideStickinessTimeout');
    };

const buildChannelSlice = (
    channelsSliceSize: number,
    selectedChannel: Channel,
    tChannels: TChannels,
): Channel[] => {
    const sliceSize =
        channelsSliceSize < tChannels.channelCount ? channelsSliceSize : tChannels.channelCount;
    const currentChannelIndex = tChannels.channels.findIndex(
        (channel) => channel.channelId === selectedChannel.channelId,
    );

    if (sliceSize !== ONE_LINE_GUIDE_CHANNEL_SLICE_SIZE) {
        return tChannels.channels;
    }

    /*
     * The selected channel should be placed in the middle of the slice.
     * This is why the next lines of code compute the beginning and end indexes of the slice.
     * */
    const numberOfChannelsBeforeCurrentChannel = Math.floor((sliceSize - 1) / 2);
    const numberOfChannelsAfterCurrentChannel =
        sliceSize - 1 - numberOfChannelsBeforeCurrentChannel;

    const channelsSliceStartIndex =
        currentChannelIndex - numberOfChannelsBeforeCurrentChannel < 0
            ? tChannels.channelCount + currentChannelIndex - numberOfChannelsBeforeCurrentChannel
            : currentChannelIndex - numberOfChannelsBeforeCurrentChannel;

    const channelsSliceEndIndex =
        (currentChannelIndex + numberOfChannelsAfterCurrentChannel) % tChannels.channelCount;

    let channelsSlice: Channel[];

    if (channelsSliceStartIndex < channelsSliceEndIndex) {
        channelsSlice = tChannels.channels.slice(
            channelsSliceStartIndex,
            channelsSliceEndIndex + 1,
        );
    } else {
        const firstSlice = tChannels.channels.slice(channelsSliceStartIndex);
        const secondSlice = tChannels.channels.slice(0, channelsSliceEndIndex + 1);
        channelsSlice = firstSlice.concat(secondSlice);
    }

    return channelsSlice;
};

const getNewSelectedChannel = (channels: Channel[], selectedChannel: Channel): Channel => {
    const previousSelectedChNumber = Number(selectedChannel.channelNumber);
    let newSelectedChannel = channels[channels.length - 1];
    for (let i = channels.length - 1; i > 0; i -= 1) {
        if (Number(channels[i].channelNumber) < previousSelectedChNumber) {
            newSelectedChannel = channels[i];
            break;
        }
    }
    return newSelectedChannel;
};

export const getOneLineGuideChannels =
    (selectedChannel: Channel, reFetchCells = false) =>
    (dispatch: Function, getState: Function) => {
        const { channelsType } = getState().channelOptionsOverlay;
        const services = new ServicesClientImpl();
        const fetchChannels = services.fetchChannels.bind(services);
        let newSelectedChannel = selectedChannel;
        RetryMechanism.callServiceWithRetry<TChannels>(
            fetchChannels,
            [channelsType],
            <RetryOptions>DEFAULT_RETRY_OPTIONS,
        )
            .then((response: TChannels) => {
                // Check to see if the previous selected channels is still in the list;
                // provides a fallback in the case it isn't
                const channelFound = response.channels.find(
                    (channel) => selectedChannel.channelId === channel.channelId,
                );
                if (!channelFound) {
                    newSelectedChannel = getNewSelectedChannel(response.channels, selectedChannel);
                    dispatch(setOneLineGuideSelectedChannelAction(newSelectedChannel));
                }
                const channelsSlice = buildChannelSlice(
                    ONE_LINE_GUIDE_CHANNEL_SLICE_SIZE,
                    newSelectedChannel,
                    response,
                );

                dispatch(setOneLineGuideEmptyAction(false));
                dispatch(setOneLineGuideChannelsAction(channelsSlice));
                if (reFetchCells) {
                    dispatch(getMetadataForChannel(newSelectedChannel, new DateTime()));
                }
            })
            .catch((error) => {
                if (error instanceof TypedChannelsNotFoundError) {
                    const favorites =
                        channelsType === Locale.getString(LocaleKeys.GUIDE_CHANNEL_TYPES_FAVORITES);

                    if (favorites) {
                        dispatch(setOneLineGuideEmptyAction(true));
                        return;
                    }
                }
                Diagnostics.error(
                    TAG,
                    'Error while fetching channels Failed to get guide OneLineGuide.getChannels() -> services.fetchChannels',
                );
                Diagnostics.error(TAG, 'stacktrace:', error);
                Log.e(TAG, error);
            });
    };

export const getOneLineGuideRowMetadata =
    (
        selectedChannel: Channel,
        anchorTime: DateTime,
        count: number,
        isPastMode: boolean,
        timeStamp?: DateTime,
    ) =>
    (dispatch: Function, getState: Function) => {
        const { currentAiringCell, currentSelectedCell, error } = getState().oneLineGuide;

        const { stationId } = selectedChannel;
        const offset = ONE_LINE_GUIDE_OFFSET_FROM_ORIGIN;

        const networkCallId = UniqueIdentifier.getNextOneLineGuideCellsNetworkCallId();
        const sThrowable: string = 'fetchOneLineGuideRowMetadata network call id mis-matched';

        const services: ServicesClientImpl = new ServicesClientImpl();
        const fetchRowMetadata = services.fetchOneLineGuideRowMetadata.bind(services);

        dispatch(setOneLineGuideIsContentFetched(true));

        RetryMechanism.callServiceWithRetry<TOneLineGuideRow>(
            fetchRowMetadata,
            [stationId, anchorTime, count, offset, isPastMode, networkCallId],
            <RetryOptions>DEFAULT_RETRY_OPTIONS,
        )
            .then((response: TOneLineGuideRow) => {
                const { areFutureCellsReceived, arePastCellsReceived } = getState().oneLineGuide;

                const lastNetworkCallId = UniqueIdentifier.getOneLineGuideCellsNetworkCallId();
                // We could have two calls on flight at the same time
                if (
                    response.networkCallId !== lastNetworkCallId &&
                    response.networkCallId !== lastNetworkCallId - 1
                ) {
                    throw new Error(sThrowable);
                }
                let onelineGuideRow = response.oneLineGuideRow;

                if (!currentAiringCell) dispatch(setAiringNowCell(onelineGuideRow, new DateTime()));

                if (!currentSelectedCell) {
                    dispatch(
                        isPastMode
                            ? setOneLineGuideArePastCellsReceived(true)
                            : setOneLineGuideAreFutureCellsReceived(true),
                    );
                }

                if (isPastMode) onelineGuideRow = onelineGuideRow.reverse();
                dispatch(setNewCells(onelineGuideRow, isPastMode));

                if (
                    !currentSelectedCell &&
                    ((isPastMode && areFutureCellsReceived) ||
                        (!isPastMode && arePastCellsReceived)) &&
                    timeStamp
                ) {
                    dispatch(setSelectedCell(timeStamp));
                }

                if (error !== null) dispatch(setOneLineGuideError(null));
            })
            .catch((fetchError) => {
                Diagnostics.error(
                    TAG,
                    'Error while fetching Metadata Failed to get guide OneLineGuide.oneLineGuideCells() -> services.fetchOneLineGuideRowMetadata',
                );
                Diagnostics.error(TAG, 'stacktrace:', fetchError?.stack);
                Log.e(TAG, fetchError?.message);

                dispatch(setOneLineGuideIsContentFetched(false));

                if (fetchError?.message === sThrowable) return;

                const callError = fetchError?.response?.data;
                const oneLineGuideContentError = new GuideRowsError(
                    callError?.message,
                    callError?.code,
                );
                OperationsLogger.logUIErrorCode(oneLineGuideContentError.getCode());
                dispatch(setOneLineGuideError(oneLineGuideContentError));
                dispatch(showWhisper());
            });
    };

const getAiringAtGivenTimeCell = (cellList: OneLineGuideCell[], time: DateTime) => {
    return cellList.find((cell: OneLineGuideCell) => {
        const startTime = cell.startTime.getTime();
        const endTime = cell.startTime.getTime() + cell.duration * 1000;

        return startTime <= time.getTime() && time.getTime() < endTime;
    });
};

export const setAiringNowCell =
    (cellList: OneLineGuideCell[], time: DateTime) => (dispatch: Function, getState: Function) => {
        const { oneLineGuideAiringNowTimeout } = getState().oneLineGuide;
        const airingNowCell: OneLineGuideCell | undefined = getAiringAtGivenTimeCell(
            cellList,
            time,
        );

        if (!airingNowCell) return;

        const airingNowCellEndTime = airingNowCell.startTime.addSeconds(airingNowCell.duration);
        const airingNowCellLeftPeriodUntilEnd =
            airingNowCellEndTime.getTime() - new DateTime().getTime();

        const timeout = setTimeout(() => {
            dispatch(setAiringNowCell(cellList, new DateTime()));
        }, airingNowCellLeftPeriodUntilEnd);

        // To stop a race condition, we have to clear the old airing now cell timeout
        clearTimeout(oneLineGuideAiringNowTimeout);
        dispatch(setOneLineGuideCellAiringNowAction(airingNowCell));
        dispatch(updateOneLineGuideAiringNowTimeoutAction(timeout));
    };

export const setSelectedCell = (time: DateTime) => (dispatch: Function, getState: Function) => {
    const { cellList, isFutureMode } = getState().oneLineGuide;

    const selectedCell: OneLineGuideCell | undefined = getAiringAtGivenTimeCell(cellList, time);

    if (!selectedCell) {
        dispatch(setOneLineGuideCellSelectedAction(cellList[cellList.length - 1]));
        return;
    }
    dispatch(setOneLineGuideCellSelectedAction(selectedCell));

    const currentSelectedCellStartTime = selectedCell?.startTime.getTime();
    const currentSelectedCellEndTime = currentSelectedCellStartTime + selectedCell.duration * 1000;
    const currentAiringTime = new DateTime().getTime();

    const isSelectedCellAiringNow =
        currentSelectedCellStartTime <= currentAiringTime &&
        currentAiringTime < currentSelectedCellEndTime;

    if (isSelectedCellAiringNow) {
        dispatch(setOneLineGuideTimeStamp(new DateTime()));
    }

    if (!currentSelectedCellStartTime || !currentAiringTime) return;

    if (
        currentSelectedCellStartTime < currentAiringTime &&
        currentSelectedCellEndTime < currentAiringTime &&
        isFutureMode
    ) {
        dispatch(setOneLineGuideIsFutureModeAction(false));
    } else if (currentSelectedCellEndTime >= currentAiringTime && !isFutureMode) {
        dispatch(setOneLineGuideIsFutureModeAction(true));
    }
};

export const setNewCells =
    (newCells: OneLineGuideCell[], isPastMode: boolean) =>
    (dispatch: Function, getState: Function) => {
        const { cellList } = getState().oneLineGuide;

        const cellsToAdd: OneLineGuideCell[] = [];

        const slicedArrayStartIndex = isPastMode ? 0 : Math.max(0, cellList.length - 9);
        const slicedArrayEndIndex = isPastMode ? Math.min(8, cellList.length) : cellList.length;

        const slicedArray = cellList.slice(slicedArrayStartIndex, slicedArrayEndIndex);
        const offerIds = slicedArray.map((cell) => cell.offerId);
        const set: Set<string> = new Set(offerIds);

        newCells.forEach((element: OneLineGuideCell) => {
            if (
                !set.has(element.offerId) &&
                (isCellWithinThresholds(element) || isCellAtTheEdge(element))
            ) {
                cellsToAdd.push(element);
            }
        });

        dispatch(setOneLineGuideCellsAppendAction(cellsToAdd, isPastMode));
        dispatch(setOneLineGuideIsContentFetched(false));
    };

export const getMetadataForChannel =
    (channel: Channel, timeStamp: DateTime) => (dispatch: Function) => {
        dispatch(setOneLineGuideCellsDeleteAction());
        dispatch(setOneLineGuideArePastCellsReceived(false));
        dispatch(setOneLineGuideAreFutureCellsReceived(false));

        // Fetch past shows
        dispatch(
            getOneLineGuideRowMetadata(
                channel,
                timeStamp.floorByMinutes(30),
                ONE_LINE_GUIDE_FETCH_ASSETS_NUMBER,
                true,
                timeStamp,
            ),
        );

        // Fetch the future and current shows
        dispatch(
            getOneLineGuideRowMetadata(
                channel,
                timeStamp.floorByMinutes(30),
                ONE_LINE_GUIDE_FETCH_ASSETS_NUMBER,
                false,
                timeStamp,
            ),
        );
    };

export const getOneLineGuidePastThreshold = (): number => {
    const service = new ServicesClientImpl();
    const pastOneLineGuideDaysNumber =
        service.getBrandingUiInfoForKey(BrandingUiInfoKeys.EPG_LOOK_BACK_HOURS) ||
        ONE_LINE_GUIDE_DEFAULT_PAST_DAYS;

    return new DateTime().subtractHours(pastOneLineGuideDaysNumber).floorByMinutes(30).getTime();
};

export const getOneLineGuideFutureThreshold = (): number => {
    return new DateTime().addHours(ONE_LINE_GUIDE_DEFAULT_FUTURE_DAYS).floorByMinutes(30).getTime();
};

export const isCellWithinThresholds = (cell: OneLineGuideCell) => {
    const startTime = cell.startTime.getTime();
    return (
        getOneLineGuidePastThreshold() <= startTime && startTime <= getOneLineGuideFutureThreshold()
    );
};

const isCellAtTheEdge = (cell: OneLineGuideCell) => {
    const startTime = cell.startTime.getTime();
    const endTime = startTime + cell.duration * 1000;
    const pastEdge = getOneLineGuidePastThreshold();
    const futureEdge = getOneLineGuideFutureThreshold();

    return (
        (startTime <= pastEdge && pastEdge < endTime) ||
        (startTime < futureEdge && futureEdge <= endTime)
    );
};

// Refresh the channel list with the latest favorite channels
// and move the selection to the nearest channel (because the old one is removed)
export const refreshOneLineGuideAfterChannelSettingsUpdate =
    (isFavorite: boolean) => (dispatch: Function, getState: Function) => {
        const { channels, selectedChannel } = getState().oneLineGuide;

        const currentChannelIndex = channels.findIndex(
            (channel) => channel.channelId === selectedChannel.channelId,
        );

        let nextChannelIndex = currentChannelIndex - 1;
        if (nextChannelIndex < 0) {
            nextChannelIndex = channels.length - Math.abs(nextChannelIndex);
        }

        if (!isFavorite && channels.length === 1) {
            dispatch(getOneLineGuideChannels(channels[nextChannelIndex]));
            dispatch(updateOneLineGuideInactivityTimeout());
            return;
        }

        dispatch(setOneLineGuideSelectedChannelAction(channels[nextChannelIndex]));
        dispatch(getOneLineGuideChannels(channels[nextChannelIndex]));
        dispatch(updateOneLineGuideInactivityTimeout());
    };

export const getOneLineGuideSingleChannel = async (channelId: string, channelType: string) => {
    const services = new ServicesClientImpl();
    const fetchChannels = services.fetchChannels.bind(services);
    let channelResponse: Channel | null = null;

    try {
        const res = await RetryMechanism.callServiceWithRetry<TChannels>(
            fetchChannels,
            [channelType],
            <RetryOptions>DEFAULT_RETRY_OPTIONS,
        );

        const channelFound = res.channels.find((channel) => channelId === channel.channelId);
        if (channelFound) {
            channelResponse = channelFound;
        }
    } catch (error) {
        Diagnostics.error(
            TAG,
            'Error while fetching channels Failed to get guide OneLineGuide.getChannels() -> services.fetchChannels',
        );
        Diagnostics.error(TAG, 'stacktrace:', error);
    }

    return channelResponse;
};
